import {useLocation, useNavigate, useParams} from 'react-router-dom';
import useOrder from '../hooks/use-order';
import Loading from 'shared/migration-components/loading';
import styled, {useTheme} from 'styled-components';
import dateFnsService from 'shared/services/date-fns-service';
import formatAddress from 'modules/addresses/util/format-address';
import {
  OrderOriginEnum,
  OrderProductProps,
  OrderStatus,
  OrderTypeEnum,
} from '../types';
import toBrl from 'shared/util/to-brl';
import {IoArrowBack, IoPersonCircle, IoReload, IoStar} from 'react-icons/io5';

import {PiForkKnifeFill} from 'react-icons/pi';
import {GiFullMotorcycleHelmet} from 'react-icons/gi';
import {MdDateRange, MdDeliveryDining, MdEditSquare} from 'react-icons/md';
import {RiMoneyDollarCircleLine} from 'react-icons/ri';
import StatusIcon from '../components/status-icon';
import getOrderStatusLabel from '../util/get-order-status-label';
import OrderStatusSelect from '../components/order-status-select';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import RetainedEarningsSelect from '../components/retained-earnings-select';
import {MediumText} from 'shared/components/text';
import ChangeSentSelect from '../components/change-sent-select';
import CircleButton from 'shared/migration-components/circle-button';

const NewOrderDetails: React.FC = () => {
  const {id} = useParams();
  const {data, isLoading} = useOrder(id!);
  const {colors} = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const {accessControl} = useAccessControls();

  if (isLoading)
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
        }}>
        <Loading size={22} />
      </div>
    );

  const {
    number,
    company,
    user,
    address,
    payment,
    statuses,
    deliveryMan,
    products,
    currentStatusName,
    type,
    deliveryPrice,
    createdAt,
    origin,
    guestUser,
    observation,
  } = data;

  const getProductPrice = (product: OrderProductProps) => {
    const additionalsPrice =
      product.additionalGroups?.reduce(
        (total, additionalGroup) => total + additionalGroup.total,
        0,
      ) ?? 0;

    return product.price + additionalsPrice;
  };

  return (
    <Container>
      <Content>
        <Info>
          <Column>
            <BackButton
              onClick={() => navigate(location.pathname.replace(id!, ''))}>
              <IoArrowBack color={colors.text} />
              <BackTitle>Voltar</BackTitle>
            </BackButton>
            <Row
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 5,
              }}>
              <Row style={{gap: 5}}>
                <Title>Pedido #{number}</Title>
                {!accessControl?.companyId && (
                  <CircleButton
                    icon={<MdEditSquare />}
                    tooltip="Alterar pedido"
                    onClick={() => navigate(`/orders/${id}/cart`)}
                  />
                )}
              </Row>
              <OrderStatusSelect {...data} />
            </Row>
            <Description>Uau! Delivery</Description>
          </Column>
          <Ratting>
            <Column
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <img
                style={{borderRadius: 25}}
                width={50}
                height={50}
                src={company.logo}
              />
            </Column>
            <Column>
              <LeadingText>{company.name}</LeadingText>
              <Text>{company?.phone}</Text>
            </Column>
            <Column style={{alignItems: 'center', gap: 3}}>
              <LeadingText style={{textAlign: 'center', fontSize: 20}}>
                {company.ratting ?? 'N/A'}
              </LeadingText>
              {company.ratting && (
                <Row>
                  <IoStar color={colors.secondary} />
                  <IoStar color={colors.secondary} />
                  <IoStar color={colors.secondary} />
                  <IoStar color={colors.secondary} />
                  <IoStar color={colors.secondary} />
                </Row>
              )}
            </Column>
            <Column style={{alignItems: 'center'}}>
              <LeadingText style={{textAlign: 'center', fontSize: 20}}>
                {company.rattings?.length}
              </LeadingText>
              <Text>avaliações</Text>
            </Column>
          </Ratting>
          <Title>Sobre o pedido</Title>
          <List>
            <Row style={{alignItems: 'start', gap: 10}}>
              <IoReload size={20} />
              <Column>
                <Text>Status</Text>
                <RegularText>
                  {getOrderStatusLabel(currentStatusName)}
                </RegularText>
              </Column>
            </Row>
            <Row style={{alignItems: 'start', gap: 10}}>
              <MdDateRange size={20} />
              <Column>
                <Text>Criado em</Text>
                <RegularText>
                  {dateFnsService.dateTimeFormatWithDistance(createdAt)}
                </RegularText>
              </Column>
            </Row>
            <Row style={{alignItems: 'start', gap: 10}}>
              <PiForkKnifeFill size={20} />
              <Column>
                <Text>Pedido lançado?</Text>
                <RegularText>
                  {origin === OrderOriginEnum.system ? 'Sim' : 'Não'}
                </RegularText>
              </Column>
            </Row>
            <RetainedEarningsSelect {...data} />
            {data.payment.name === 'Dinheiro' && data.payment.change! > 0 && (
              <ChangeSentSelect {...data} />
            )}
            <Row style={{alignItems: 'start', gap: 10}}>
              <MdDeliveryDining size={20} />
              <Column>
                <Text>Tipo</Text>
                <RegularText>
                  {type === OrderTypeEnum.delivery ? 'Entrega' : 'Retirada'}
                </RegularText>
              </Column>
            </Row>
            {accessControl?.cityId && (
              <Row
                style={{alignItems: 'start', gap: 10, cursor: 'pointer'}}
                onClick={() => navigate('occurrences')}>
                <MdDeliveryDining size={20} />
                <Column>
                  <Text>Ocorrências</Text>
                  <RegularText>Valores extras do pedido</RegularText>
                </Column>
              </Row>
            )}
          </List>
          <Line />
          <Row style={{gap: 5, alignItems: 'center'}}>
            <IoPersonCircle size={40} />
            {/* <img
              style={{borderRadius: 25, objectFit: 'cover'}}
              width="50"
              height="50"
              src="https://a0.muscache.com/im/pictures/user/931f6d99-abaf-4784-9824-e7f38c7711c7.jpg"
            /> */}
            <Column>
              <LeadingText>{user?.name ?? guestUser?.name}</LeadingText>
              <Text>{user?.phone ?? guestUser?.phone}</Text>
            </Column>
          </Row>
          <Line />
          <Row style={{gap: 10}}>
            <RiMoneyDollarCircleLine size={20} />
            <Column
              style={{cursor: 'pointer'}}
              onClick={() =>
                !accessControl?.companyId && navigate('payment-method')
              }>
              <LeadingText>Pagamento</LeadingText>
              <Text>
                {payment.name}{' '}
                {payment.change
                  ? ` - Troco para: ${toBrl(payment.change)}`
                  : ''}{' '}
                {payment.pixChange
                  ? ` - Pix para troco: ${payment.pixChange}`
                  : ''}
              </Text>
            </Column>
          </Row>
          {address && (
            <Row style={{gap: 10}}>
              <MdDeliveryDining size={20} />
              <Column>
                <LeadingText>Endereço de entrega</LeadingText>
                <Text>{formatAddress(address)}</Text>
              </Column>
            </Row>
          )}
          {!address && (
            <Row style={{gap: 10}}>
              <MdDeliveryDining />
              <Column>
                <LeadingText>Endereço de retirada</LeadingText>
                <Text>{formatAddress(company.address)}</Text>
              </Column>
            </Row>
          )}
          <Row style={{gap: 10}}>
            <GiFullMotorcycleHelmet size={18} />
            <Column>
              <LeadingText>Entregador</LeadingText>
              <Text>
                {deliveryMan?.name} - {deliveryMan?.phone}
              </Text>
            </Column>
          </Row>
          <Line />
          <Title>Produtos</Title>
          {products.map((product) => {
            const additionalsPrice =
              product.additionalGroups?.reduce(
                (total, additionalGroup) => total + additionalGroup.total,
                0,
              ) ?? 0;

            const totalWithSale: number | undefined = product.salePrice
              ? (product.salePrice + additionalsPrice) * product.quantity
              : undefined;

            const total = (product.price + additionalsPrice) * product.quantity;

            return (
              <Row style={{gap: 10}} key={product.id}>
                <LeadingText>{product.quantity}x</LeadingText>
                <Column style={{flex: 1}}>
                  <LeadingText>{product.name}</LeadingText>
                  {product.size && <Text>Tamanho: {product.size}</Text>}
                  {product.additionalGroups && <div style={{height: 5}} />}
                  {product.additionalGroups?.reduce(
                    (components, {additionals}) => [
                      ...components,
                      ...additionals.map(({id, quantity, name}) => (
                        <Text key={id}>
                          {quantity}x {name}
                        </Text>
                      )),
                    ],
                    [] as React.ReactNode[],
                  )}
                </Column>
                {totalWithSale && (
                  <Text style={{color: colors.green, fontWeight: 500}}>
                    {toBrl(totalWithSale)}
                  </Text>
                )}
                <Text
                  style={{
                    fontSize: totalWithSale ? 12 : undefined,
                    textDecoration: totalWithSale ? 'line-through' : undefined,
                    marginTop: 1,
                  }}>
                  {toBrl(total)}
                </Text>
              </Row>
            );
          })}
          {observation && (
            <>
              <Line />
              <Column style={{gap: 5}}>
                <MediumText>Observação:</MediumText>
                <RegularText
                  style={{
                    color: colors.error,
                    fontWeight: 'bold',
                    whiteSpace: 'pre-line',
                  }}>
                  {observation}
                </RegularText>
              </Column>
            </>
          )}
        </Info>
        <SideBar>
          <Checkout>
            <Title>Detalhe da compra</Title>
            <Row style={{justifyContent: 'space-between'}}>
              <Text>Sub total</Text>
              <Text>
                {toBrl(
                  data.total - (data.freeDelivery ? 0 : data.deliveryPrice),
                )}
              </Text>
            </Row>
            {data.couponTotal! > 0 && (
              <Row style={{justifyContent: 'space-between'}}>
                <Text>Desconto</Text>
                <Text>- {toBrl(data.couponTotal!)}</Text>
              </Row>
            )}
            {data.balanceTotal! > 0 && (
              <Row style={{justifyContent: 'space-between'}}>
                <Text>Carteira</Text>
                <Text>- {toBrl(data.balanceTotal!)}</Text>
              </Row>
            )}
            <Row style={{justifyContent: 'space-between'}}>
              <Text>Taxa de entrega</Text>
              {!data.freeDelivery && <Text>{toBrl(deliveryPrice)}</Text>}
              {data.freeDelivery && <Text color={colors.success}>Grátis</Text>}
            </Row>
            <Row style={{justifyContent: 'space-between'}}>
              <Text>Total</Text>
              <Text>{toBrl(Math.max(data.paidTotal, 0))}</Text>
            </Row>
          </Checkout>
          <History>
            <SubTitle>Histórico</SubTitle>
            <Timeline>
              {statuses.map((status, index) => (
                <TimelineItem key={status.name}>
                  <TimelineIcon>
                    <TimelineIconBox>
                      <StatusIcon
                        {...{
                          status: status.name,
                        }}
                      />
                    </TimelineIconBox>
                    {statuses.length - 1 !== index && <TimelineLine />}
                  </TimelineIcon>
                  <Column>
                    <RegularText style={{fontSize: 14}}>
                      {status.name === OrderStatus.delivery_man_assigned
                        ? `Entregador ${status.deliveryMan} vinculado`
                        : getOrderStatusLabel(status.name)}{' '}
                      {status.user && <Text> - {status.user}</Text>}
                    </RegularText>
                    <Text style={{fontSize: 12}}>
                      {dateFnsService.dateTimeFormat(status.date)}
                    </Text>
                  </Column>
                </TimelineItem>
              ))}
            </Timeline>
          </History>
        </SideBar>
      </Content>
    </Container>
  );
};

export default NewOrderDetails;

const Container = styled.div`
  max-width: 100vw;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.card};
  color: ${(props) => props.theme.colors.text};
`;

export const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  margin-bottom: 15px;

  svg {
    font-size: 24px;
  }
`;

export const BackTitle = styled.h1`
  color: ${(props) => props.theme.colors.text};
  font-size: 18px;
`;

const Content = styled.div`
  width: 1100px;
  padding: 15px;
  padding-top: 0;
  display: grid;
  grid-template-columns: 680px 1fr;
  gap: 30px;
`;

const Info = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SideBar = styled.div`
  padding: 15px;
  padding-top: 60px;
  position: sticky;
  top: 30px;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Checkout = styled.div`
  border: 1px solid rgba(128, 128, 128, 0.4);
  box-shadow: rgba(0, 0, 0, 0.12) 0px 6px 16px;
  border-radius: 10px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const History = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
`;

const SubTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
`;

const Description = styled.p``;

const LeadingText = styled.h3`
  color: ${(props) => props.theme.colors.text};
  font-weight: 600;
  line-height: normal;
`;

const Text = styled.text`
  color: ${(props) => props.theme.colors.text};
  font-weight: 300;
  line-height: normal;
`;

const RegularText = styled.text`
  color: ${(props) => props.theme.colors.text};
  font-weight: 500;
`;

const Line = styled.div`
  height: 0.5px;
  width: 100%;
  background-color: ${(props) => props.theme.colors.border};
`;

const Ratting = styled.div`
  border: 1px solid ${(props) => props.theme.colors.border};
  padding: 20px 15px;
  border-radius: 15px;
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr;
  gap: 15px;
  align-items: center;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimelineItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  gap: 15px;
`;

const TimelineIconBox = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 12.5px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${(props) => props.theme.colors.primary};
`;

const TimelineIcon = styled.div`
  display: flex;
  flex-direction: column;
  height: 60px;
  align-items: center;
`;

const TimelineLine = styled.div`
  width: 1px;
  background-color: ${(props) => props.theme.colors.gray};
  flex: 1;
`;
